import type { HeaterType, RoofKind } from "../../engines/types";
import type { SaunaType } from "../../types/design";
import {
	CC_M,
	getHeaterWidthM,
	INTERIOR_LINING_D_M,
	JOIST_W_M,
	PANEL_T_M,
	STUD_D_M,
	toMm,
} from "../geometry/buildingDimensions";
import type { HeaterSelection } from "../heater/heaterCatalog";
import { roofFramePositions } from "../roof/roofFramePositions";
import { CHIMNEY_OUTER_DIAMETER_MM } from "./chimneyStack";

// Physical dimensions come from domain/geometry/buildingDimensions, which the
// engines re-export as ENGINE_*. These were local mm literals until 2026-07-28;
// PANEL_THICKNESS_MM had drifted to 15 against the registry's 20, so every room
// measured 10 mm too wide here and admin rule overrides were ignored outright.
const STUD_DEPTH_MM = toMm(STUD_D_M);
const INTERIOR_LINING_MM = toMm(INTERIOR_LINING_D_M);
const PANEL_THICKNESS_MM = toMm(PANEL_T_M);
const JOIST_WIDTH_MM = toMm(JOIST_W_M);
const JOIST_CC_MM = toMm(CC_M);
const WALL_MARGIN_MM = 150;
const BENCH_EXCLUSION_MM = 1000;
export const CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM = 100;
const LOWER_BENCH_DEPTH_MM = 400;
const TWO_ROOM_BENCH_CENTER_OFFSET_MM = 900;
const TWO_ROOM_BENCH_WALKWAY_CLEARANCE_MM = 400;
const FRONT_SHIELD_OFFSET_MM = 120;

export interface HeaterPlacementSlot {
	id: string;
	centerZMm: number;
	clearWidthMm: number;
	label: string;
}

export interface HeaterPlacementResult {
	mode: "fixed" | "slot-select";
	selectedCenterZMm: number;
	selectedSlotId?: string;
	snappedToValidSlot: boolean;
	validSlots: HeaterPlacementSlot[];
}

interface CalculateHeaterPlacementInput {
	saunaType: SaunaType;
	widthM: number;
	lengthM: number;
	heaterType: HeaterType;
	/** A catalog model's width replaces the type default (issue 159). */
	heaterModelId?: string | null;
	roofKind: RoofKind;
	partitionSplit?: number;
	preferredCenterZMm?: number;
}

function getHeaterWidthMm(heater: HeaterSelection) {
	return toMm(getHeaterWidthM(heater));
}

function getRectangularDefaultCenterZMm(
	lengthMm: number,
	heaterWidthMm: number,
) {
	const innerLengthMm =
		lengthMm -
		2 * STUD_DEPTH_MM -
		2 * INTERIOR_LINING_MM -
		2 * PANEL_THICKNESS_MM;
	return Math.round(innerLengthMm / 2 - heaterWidthMm / 2 - WALL_MARGIN_MM);
}

function getTwoRoomDefaultCenterZMm(
	lengthMm: number,
	partitionSplit: number,
	heaterWidthMm: number,
) {
	const dividerZMm = -lengthMm / 2 + lengthMm * partitionSplit;
	return Math.round(
		dividerZMm -
			STUD_DEPTH_MM -
			INTERIOR_LINING_MM -
			heaterWidthMm / 2 -
			WALL_MARGIN_MM,
	);
}

function getRectangularPlacementRangeMm(
	lengthMm: number,
	heaterWidthMm: number,
) {
	const innerLengthMm =
		lengthMm -
		2 * STUD_DEPTH_MM -
		2 * INTERIOR_LINING_MM -
		2 * PANEL_THICKNESS_MM;
	return {
		minZMm: Math.round(
			-innerLengthMm / 2 +
				BENCH_EXCLUSION_MM +
				WALL_MARGIN_MM +
				heaterWidthMm / 2,
		),
		maxZMm: getRectangularDefaultCenterZMm(lengthMm, heaterWidthMm),
	};
}

function getTwoRoomPlacementRangeMm(
	lengthMm: number,
	partitionSplit: number,
	heaterWidthMm: number,
) {
	const innerBackWallZMm =
		-lengthMm / 2 + STUD_DEPTH_MM + INTERIOR_LINING_MM + PANEL_THICKNESS_MM;
	const lowerBenchFrontEdgeZMm =
		innerBackWallZMm +
		TWO_ROOM_BENCH_CENTER_OFFSET_MM +
		LOWER_BENCH_DEPTH_MM / 2;
	return {
		minZMm: Math.round(
			lowerBenchFrontEdgeZMm +
				TWO_ROOM_BENCH_WALKWAY_CLEARANCE_MM +
				heaterWidthMm / 2,
		),
		maxZMm: Math.round(
			-lengthMm / 2 +
				lengthMm * partitionSplit -
				heaterWidthMm / 2 -
				FRONT_SHIELD_OFFSET_MM,
		),
	};
}

function getRoofJoistCentersMm(lengthMm: number) {
	const lengthM = lengthMm / 1000;
	const joistWidthM = JOIST_WIDTH_MM / 1000;
	const count = Math.ceil((lengthM - joistWidthM) / (JOIST_CC_MM / 1000)) + 1;
	const spacingMm = count > 1 ? (lengthMm - JOIST_WIDTH_MM) / (count - 1) : 0;

	return Array.from(
		{ length: count },
		(_, index) => -lengthMm / 2 + JOIST_WIDTH_MM / 2 + spacingMm * index,
	);
}

function getValidSlots(
	lengthMm: number,
	minZMm: number,
	maxZMm: number,
): HeaterPlacementSlot[] {
	const joistCenters = getRoofJoistCentersMm(lengthMm);
	const clearWidthMm = Math.round(JOIST_CC_MM - JOIST_WIDTH_MM);
	const minRequiredClearWidthMm =
		CHIMNEY_OUTER_DIAMETER_MM + 2 * CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM;

	if (clearWidthMm < minRequiredClearWidthMm) {
		return [];
	}

	return joistCenters.flatMap((center, index) => {
		if (index >= joistCenters.length - 1) return [];
		const bayCenterMm = Math.round((center + joistCenters[index + 1]) / 2);
		if (bayCenterMm < minZMm || bayCenterMm > maxZMm) return [];
		return [
			{
				id: `bay-${index + 1}`,
				centerZMm: bayCenterMm,
				clearWidthMm,
				label: `Fack ${index + 1}`,
			},
		];
	});
}

function getNearestSlot(slots: HeaterPlacementSlot[], targetZMm: number) {
	return slots.reduce((closest, slot) => {
		if (!closest) {
			return slot;
		}

		return Math.abs(slot.centerZMm - targetZMm) <
			Math.abs(closest.centerZMm - targetZMm)
			? slot
			: closest;
	}, slots[0]);
}

export function calculateHeaterPlacement(
	input: CalculateHeaterPlacementInput,
): HeaterPlacementResult {
	const lengthMm = Math.round(input.lengthM * 1000);
	const heaterWidthMm = getHeaterWidthMm(input);

	if (input.saunaType === "tunna" || input.roofKind === "barrel") {
		return {
			mode: "fixed",
			selectedCenterZMm: Math.round(
				-lengthMm / 2 + heaterWidthMm / 2 + WALL_MARGIN_MM,
			),
			snappedToValidSlot: false,
			validSlots: [],
		};
	}

	const defaultCenterZMm =
		input.saunaType === "tvarum"
			? getTwoRoomDefaultCenterZMm(
					lengthMm,
					input.partitionSplit ?? 0.5,
					heaterWidthMm,
				)
			: getRectangularDefaultCenterZMm(lengthMm, heaterWidthMm);

	if (input.heaterType !== "wood") {
		return {
			mode: "fixed",
			selectedCenterZMm: defaultCenterZMm,
			snappedToValidSlot: false,
			validSlots: [],
		};
	}

	const range =
		input.saunaType === "tvarum"
			? getTwoRoomPlacementRangeMm(
					lengthMm,
					input.partitionSplit ?? 0.5,
					heaterWidthMm,
				)
			: getRectangularPlacementRangeMm(lengthMm, heaterWidthMm);
	const validSlots = getValidSlots(lengthMm, range.minZMm, range.maxZMm);

	if (validSlots.length === 0) {
		return {
			mode: "fixed",
			selectedCenterZMm: defaultCenterZMm,
			snappedToValidSlot: false,
			validSlots: [],
		};
	}

	const preferredCenterZMm = input.preferredCenterZMm ?? defaultCenterZMm;
	const nearestSlot = getNearestSlot(validSlots, preferredCenterZMm);

	return {
		mode: "slot-select",
		selectedCenterZMm: nearestSlot.centerZMm,
		selectedSlotId: nearestSlot.id,
		snappedToValidSlot: nearestSlot.centerZMm !== preferredCenterZMm,
		validSlots,
	};
}

/**
 * Smallest shift (metres, ≥ 0) along `step` that puts a flue at `centerZM`
 * into a roof-joist bay with `CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM` to both
 * joists, or null when no bay within `maxShiftM` is wide enough. Joist
 * positions are the drawn ones (`roofFramePositions`), so the model and this
 * answer measure the same frame.
 */
export function chimneyBayShiftM(
	centerZM: number,
	step: 1 | -1,
	lengthM: number,
	maxShiftM: number,
): number | null {
	// +1 mm: the layout rounds the stove centre to whole millimetres, and a
	// flue aimed exactly at the limit would land a fraction inside it.
	const reachM =
		JOIST_WIDTH_MM / 2000 +
		CHIMNEY_OUTER_DIAMETER_MM / 2000 +
		CHIMNEY_CLEARANCE_TO_COMBUSTIBLES_MM / 1000 +
		0.001;
	const joists = roofFramePositions(lengthM);
	let best: number | null = null;
	for (let index = 0; index < joists.length - 1; index += 1) {
		const lo = joists[index] + reachM;
		const hi = joists[index + 1] - reachM;
		if (lo > hi) continue;
		// z = centerZM + step·s must land in [lo, hi].
		const [from, to] =
			step === 1 ? [lo - centerZM, hi - centerZM] : [centerZM - hi, centerZM - lo];
		const shift = Math.max(0, from);
		if (shift <= Math.min(maxShiftM, to) + 1e-9 && (best === null || shift < best)) {
			best = shift;
		}
	}
	return best;
}
